import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";

const RSS_REFRESH_MS = 5 * 60 * 1000; // 5 min
const SLIDE_DURATION_MS = 10 * 1000;   // 10s
const NEWS_BETWEEN_TRAFFIC = 5;

/**
 * useScreenRotation
 * Fetches RSS news + active Sperrungen, builds a slide sequence
 * (3 News → 1 Stauinfo → 3 News → 1 Stauinfo → …) and rotates
 * automatically every 10 seconds. Refreshes RSS every 5 min.
 */
export default function useScreenRotation() {
  const [news, setNews] = useState([]);
  const [sperrungen, setSperrungen] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const sequenceRef = useRef([]);
  const timerRef = useRef(null);
  const rssTimerRef = useRef(null);

  const buildSequence = useCallback((newsArr, sperrungenArr) => {
    const seq = [];
    for (let i = 0; i < newsArr.length; i++) {
      seq.push({ type: "news", payload: newsArr[i] });
      // Every NEWS_BETWEEN_TRAFFIC news, show the live traffic map for 10s
      if ((i + 1) % NEWS_BETWEEN_TRAFFIC === 0) {
        seq.push({ type: "map", sperrungen: sperrungenArr });
      }
    }
    return seq;
  }, []);

  // Fetch RSS (top 5 latest) + Sperrungen
  const fetchAll = useCallback(async (isFirst = false) => {
    try {
      const [rss, sp] = await Promise.all([
        api.get("/detmold-rss").then((r) => (r.data?.items || []).slice(0, 5)).catch(() => []),
        api.get("/sperrungen", { params: { only_active: true } }).then((r) => r.data || []).catch(() => []),
      ]);
      setNews(rss);
      setSperrungen(sp);
    } catch {
      // Silent fallback
    } finally {
      if (isFirst) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(true);
    rssTimerRef.current = setInterval(() => fetchAll(false), RSS_REFRESH_MS);
    return () => { if (rssTimerRef.current) clearInterval(rssTimerRef.current); };
  }, [fetchAll]);

  // Rebuild sequence whenever news/sperrungen change
  useEffect(() => {
    sequenceRef.current = buildSequence(news, sperrungen);
    // Clamp index
    setIndex((i) => (sequenceRef.current.length > 0 ? i % sequenceRef.current.length : 0));
  }, [news, sperrungen, buildSequence]);

  // Auto-rotate
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (sequenceRef.current.length === 0) return;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % sequenceRef.current.length);
    }, SLIDE_DURATION_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [index, news, sperrungen]);

  const current = sequenceRef.current[index] || null;
  const total = sequenceRef.current.length;
  return { current, index, total, news, sperrungen, loading };
}
